// Explicit .js extensions: this module is shared with the server-side sync
// pipeline (api/lib/syncPipeline.js), and Node's ESM resolver — unlike
// webpack — will not infer them.
import { normalizeDesc } from './forecasting/series-key.js';
import { formatMerchantName, merchantKey } from './merchant.js';

// Grouping key lives with the naming pipeline: category memory has to bucket
// transactions exactly the way the UI names them, or a user who recategorizes
// "Blue Bottle Coffee" sees the next identical-looking row stay uncategorized.
export { merchantKey };

const KEYWORD_RULES = [
  { category: 'Accounts & Transfers', keywords: ['transfer', 'zelle', 'venmo', 'paypal transfer', 'ach xfer', 'wire transfer', 'internal transfer'] },
  { category: 'Credit Cards', keywords: ['credit card', 'card payment', 'autopay cc', 'cc payment', 'capital one payment', 'chase card', 'discover payment', 'amex payment'] },
  { category: 'Income', keywords: ['payroll', 'direct dep', 'direct deposit', 'salary', 'paycheck', 'employer', 'interest earned', 'dividend'] },
  { category: 'Housing', keywords: ['rent', 'mortgage', 'apartment', 'property mgmt', 'hoa', 'landlord'] },
  { category: 'Utilities', keywords: ['electric', 'power co', 'water bill', 'gas bill', 'internet', 'comcast', 'xfinity', 'at&t', 'att ', 'verizon', 't-mobile', 'tmobile', 'spectrum', 'duke energy', 'pg&e', 'pge '] },
  { category: 'Groceries', keywords: ['walmart', 'costco', 'kroger', 'safeway', 'trader joe', 'whole foods', 'wholefds', 'aldi', 'publix', 'heb', 'grocery', 'supermarket', 'food lion', 'wegmans', 'sprouts', 'market basket', 'sam\'s club', 'sams club'] },
  { category: 'Eating Out', keywords: ['mcdonald', 'starbucks', 'chipotle', 'doordash', 'grubhub', 'uber eats', 'ubereats', 'restaurant', 'cafe', 'pizza', 'taco bell', 'subway', 'dunkin', 'panera', 'wendy', 'burger king', 'domino', 'chick-fil', 'chick fil', 'dining'] },
  { category: 'Subscriptions', keywords: ['netflix', 'spotify', 'hulu', 'disney+', 'disney plus', 'apple.com/bill', 'apple com bill', 'google *youtube', 'youtube premium', 'adobe', 'microsoft*', 'microsoft ', 'dropbox', 'notion', 'github', 'openai', 'chatgpt', 'icloud', 'amazon prime', 'prime video', 'audible', 'patreon', 'gym membership', 'planet fitness'] },
  { category: 'Cars', keywords: ['shell', 'chevron', 'exxon', 'mobil', 'bp ', 'gas station', 'fuel', 'auto repair', 'jiffy lube', 'parking', 'toll', 'dmv', 'car wash', 'uber trip', 'lyft'] },
  { category: 'Health & Wellness', keywords: ['cvs', 'walgreens', 'pharmacy', 'doctor', 'medical', 'dental', 'hospital', 'clinic', 'optom', 'vision', 'urgent care', 'health ins'] },
  { category: 'Entertainment', keywords: ['movie', 'theatre', 'theater', 'cinema', 'steam', 'playstation', 'xbox', 'nintendo', 'ticketmaster', 'amc ', 'spotify'] },
  { category: 'Personal', keywords: ['amazon', 'target', 'best buy', 'home depot', 'lowes', 'ikea', 'clothing', 'apparel', 'salon', 'barber'] },
];

const TRANSFER_HINTS = ['transfer', 'zelle', 'venmo', 'paypal', 'ach', 'wire'];

function descHaystack(description) {
  return normalizeDesc(description);
}

function matchesKeywords(description, keywords) {
  // Test the raw descriptor and the normalized name: processor prefixes and
  // glued reference numbers ("TST*CHIPOTLE0231") hide keywords in the raw
  // string that are plainly visible once the name is cleaned up.
  const raw = descHaystack(description);
  const named = formatMerchantName(description).toLowerCase();
  return keywords.some((kw) => raw.includes(kw) || named.includes(kw));
}

function matchesTransfer(description) {
  return TRANSFER_HINTS.some((kw) => descHaystack(description).includes(kw));
}

export function buildCategoryLookup(pastTransactions, categories) {
  const exactLookup = new Map();
  const merchantLookup = new Map();

  pastTransactions.forEach((tx) => {
    if (!tx.category || tx.category === 'Other' || !categories.includes(tx.category)) return;

    const exact = descHaystack(tx.description);
    const key = merchantKey(tx.description);
    if (!exact && !key) return;

    if (tx.manualCategory) {
      if (exact) exactLookup.set(exact, tx.category);
      if (key) merchantLookup.set(key, tx.category);
      return;
    }

    if (exact && !exactLookup.has(exact)) exactLookup.set(exact, tx.category);
    if (key && !merchantLookup.has(key)) merchantLookup.set(key, tx.category);
  });

  return { exactLookup, merchantLookup };
}

export function matchCategory(tx, categories, lookup) {
  const { exactLookup, merchantLookup } = lookup;

  if (tx.type === 'repayment' && categories.includes('Credit Cards')) {
    return 'Credit Cards';
  }

  if (tx.type === 'income') {
    if (matchesTransfer(tx.description) && categories.includes('Accounts & Transfers')) {
      return 'Accounts & Transfers';
    }
    if (categories.includes('Income')) return 'Income';
  }

  const exact = descHaystack(tx.description);
  if (exact && exactLookup.has(exact)) {
    const cat = exactLookup.get(exact);
    if (categories.includes(cat)) return cat;
  }

  const key = merchantKey(tx.description);
  if (key && merchantLookup.has(key)) {
    const cat = merchantLookup.get(key);
    if (categories.includes(cat)) return cat;
  }

  for (const rule of KEYWORD_RULES) {
    if (!categories.includes(rule.category)) continue;
    if (matchesKeywords(tx.description, rule.keywords)) return rule.category;
  }

  return null;
}

export function resolveCategories(transactions, { categories, pastTransactions = [] }) {
  const lookup = buildCategoryLookup(pastTransactions, categories);
  const map = {};
  const needsAI = [];

  transactions.forEach((tx) => {
    const category = matchCategory(tx, categories, lookup);
    if (category) map[tx.id] = category;
    else needsAI.push(tx);
  });

  return { map, needsAI };
}
